/**
 * Submit the cart as soon as a quantity changes, instead of waiting for "Update cart".
 *
 * WooCommerce already updates the cart over AJAX — cart.js catches the click on
 * [name="update_cart"], serializes the form, swaps in the new markup and fires
 * `updated_cart_totals`. So all this does is press that button for the customer, which
 * keeps us on WooCommerce's own request, nonce and error handling.
 *
 * The one thing that needs handling on our side is that cart.js replaces the entire form —
 * inputs included — with the server's markup. Anything the customer clicks while a request is
 * in flight lives only in the outgoing DOM, so those changes are collected and written back
 * into the fresh inputs. See `pending`.
 */

// A failed request never reaches `updated_cart_totals`; without this the in-flight flag would
// stay up and quietly kill auto-update for the rest of the session.
const STUCK_UPDATE_MS = 15000;

const QTY_SELECTOR = ".woocommerce-cart-form .cart_item input.qty";

export const setupCartAutoUpdate = () => {
  if (!document.querySelector(".woocommerce-cart-form")) return;

  // Only now is the button redundant — without JS it stays the way to submit the cart.
  document.body.classList.add("cart-auto-update");

  let updating = false;
  let stuckTimer = null;
  let restoreNoticeScroll = null;

  // Quantities changed while a request was in flight, keyed by input name.
  const pending = new Map();

  /**
   * cart.js finishes every quantity update with `$.scroll_to_notices( $('[role="alert"]') )`,
   * which animates the window to the "Cart updated" message. Harmless when the customer had
   * just clicked a button at the bottom of the table, jarring when it happens on every
   * keystroke. Stubbed out for the duration of our own updates only, so coupon errors and
   * the rest of WooCommerce keep their scrolling.
   */
  const suppressNoticeScroll = () => {
    if (restoreNoticeScroll || typeof jQuery.scroll_to_notices !== "function") {
      return;
    }

    const original = jQuery.scroll_to_notices;

    jQuery.scroll_to_notices = () => {};

    restoreNoticeScroll = () => {
      jQuery.scroll_to_notices = original;
      restoreNoticeScroll = null;
    };
  };

  /**
   * Hand scrolling back to WooCommerce, but only once the cart is idle.
   *
   * cart.js scrolls from the ajax `complete` handler, so restoring on any kind of timer is a
   * race: a click landing in that window starts a new request against a stub that is about to
   * be removed, and the page jumps when that request lands. The in-flight flag is the only
   * thing that can be trusted here.
   */
  const releaseNoticeScroll = () => {
    window.setTimeout(() => {
      if (updating) return;

      restoreNoticeScroll?.();
    }, 0);
  };

  const submitUpdate = () => {
    const button = document.querySelector(
      '.woocommerce-cart-form [name="update_cart"]'
    );

    if (!button || updating) return;

    updating = true;
    suppressNoticeScroll();

    window.clearTimeout(stuckTimer);
    stuckTimer = window.setTimeout(() => {
      updating = false;
      restoreNoticeScroll?.();
    }, STUCK_UPDATE_MS);

    // cart.js keeps the button disabled until it sees an input event of its own.
    button.disabled = false;
    button.click();
  };

  /**
   * Put the quantities the customer picked mid-request back into the freshly rendered inputs.
   *
   * @return {boolean} Whether anything still differs from what the server sent back.
   */
  const restorePending = () => {
    let changed = false;

    document.querySelectorAll(QTY_SELECTOR).forEach((input) => {
      if (!pending.has(input.name)) return;

      const value = pending.get(input.name);

      // The server may have landed on that quantity by itself — nothing left to submit.
      if (input.value === value) return;

      input.value = value;
      // Refreshes the +/- disabled states and lets cart.js enable its own button.
      input.dispatchEvent(new Event("input", { bubbles: true }));
      changed = true;
    });

    pending.clear();

    return changed;
  };

  // Delegated: cart.js replaces the whole form on every update.
  document.addEventListener("change", (event) => {
    const input = event.target.closest(QTY_SELECTOR);

    if (!input) return;

    // cart.js ignores a submit while a request is in flight, and is about to throw this input
    // away — hold the quantity and replay it once the new markup is in.
    if (updating) {
      pending.set(input.name, input.value);
      return;
    }

    if (input.value === input.defaultValue) return;

    submitUpdate();
  });

  jQuery(document.body).on("updated_cart_totals", () => {
    window.clearTimeout(stuckTimer);
    updating = false;

    // Runs in the same tick as cart.js's `replaceWith`, so the stale quantity is written over
    // before the browser gets to paint it.
    if (restorePending()) {
      // The request itself waits a tick: cart.js unblocks the form from its `complete`
      // handler, which runs right after this one and would otherwise clear the guard on our
      // follow-up request instead of the finished one. The notice scroll stays stubbed until
      // that follow-up comes back.
      window.setTimeout(submitUpdate, 0);
      return;
    }

    releaseNoticeScroll();
  });
};
